/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * LLM Model Form Component
 * 
 * Form for creating and editing LLM models
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { llmModelApi, llmProviderApi } from '../../services/api';
import type { LlmModel, LlmModelCreate } from '../../types';

interface LlmModelFormProps {
  open: boolean;
  onClose: () => void;
  model?: LlmModel;
}

export const LlmModelForm: React.FC<LlmModelFormProps> = ({ open, onClose, model }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [configJson, setConfigJson] = useState('{}');
  const [capabilityMetadataJson, setCapabilityMetadataJson] = useState('{}');

  const [formData, setFormData] = useState<LlmModelCreate>({
    provider: 0,
    name: '',
    model: '',
    cost: 0, // Legacy field
    input_cost: 0,
    output_cost: 0,
    cost_threshold_tokens: 0,
    input_cost_above_threshold: 0,
    output_cost_above_threshold: 0,
    is_active: true,
    audio_input_supported: false,
    config: {},
    capability_metadata: {},
  });

  // Fetch providers for dropdown
  const { data: providersData } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => llmProviderApi.list({}),
  });

  // Initialize form data when editing
  useEffect(() => {
    if (model) {
      setFormData({
        provider: model.provider,
        name: model.name,
        model: model.model,
        cost: model.cost || 0, // Legacy
        input_cost: model.input_cost || 0,
        output_cost: model.output_cost || 0,
        cost_threshold_tokens: model.cost_threshold_tokens || 0,
        input_cost_above_threshold: model.input_cost_above_threshold || 0,
        output_cost_above_threshold: model.output_cost_above_threshold || 0,
        is_active: model.is_active,
        audio_input_supported: model.audio_input_supported === true,
        config: model.config || {},
        capability_metadata: model.capability_metadata || {},
      });
      setConfigJson(JSON.stringify(model.config || {}, null, 2));
      setCapabilityMetadataJson(JSON.stringify(model.capability_metadata || {}, null, 2));
    } else {
      // Reset form for new model
      setFormData({
        provider: 0,
        name: '',
        model: '',
        cost: 0,
        input_cost: 0,
        output_cost: 0,
        cost_threshold_tokens: 0,
        input_cost_above_threshold: 0,
        output_cost_above_threshold: 0,
        is_active: true,
        audio_input_supported: false,
        config: {},
        capability_metadata: {},
      });
      setConfigJson('{}');
      setCapabilityMetadataJson('{}');
    }
    setError(null);
  }, [model, open]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: LlmModelCreate) => llmModelApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create model');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: LlmModelCreate) => llmModelApi.update(model!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update model');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.provider) {
      setError('Please select a provider');
      return;
    }

    let parsedConfig: Record<string, any> = {};
    let parsedCapabilityMetadata: Record<string, any> = {};
    if (configJson.trim()) {
      try {
        const parsed = JSON.parse(configJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedConfig = parsed as Record<string, any>;
        } else {
          setError('Config must be a JSON object (e.g. {"key": "value"})');
          return;
        }
      } catch {
        setError('Config JSON is invalid. Please fix syntax errors.');
        return;
      }
    }
    if (capabilityMetadataJson.trim()) {
      try {
        const parsed = JSON.parse(capabilityMetadataJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedCapabilityMetadata = parsed as Record<string, any>;
        } else {
          setError('Capability metadata must be a JSON object.');
          return;
        }
      } catch {
        setError('Capability metadata JSON is invalid. Please fix syntax errors.');
        return;
      }
    }

    const payload: LlmModelCreate = {
      ...formData,
      config: parsedConfig,
      capability_metadata: parsedCapabilityMetadata,
    };

    if (model) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleChange = (field: keyof LlmModelCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "md"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
              {model ? 'Edit LLM Model' : 'Add LLM Model'}
            </Typography>
            {!isMobile && (
              <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(e) => handleChange('provider', Number(e.target.value))}
                required
                disabled={isLoading}
                InputProps={{ style: { fontSize: 16 } }}
              >
                <MenuItem value={0}>Select Provider</MenuItem>
                {providersData?.results.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                disabled={isLoading}
                helperText="Display name for the model"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model ID"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                required
                disabled={isLoading}
                helperText="API model identifier (e.g., gpt-4)"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Input Cost per 1M tokens"
                value={formData.input_cost}
                onChange={(e) => handleChange('input_cost', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cost in USD per 1M input tokens"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Output Cost per 1M tokens"
                value={formData.output_cost}
                onChange={(e) => handleChange('output_cost', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cost in USD per 1M output tokens"
              />
            </Grid>

            {/* Tiered Pricing Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Tiered Pricing (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For models with different rates above a token threshold (e.g. Gemini)
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Threshold (tokens)"
                value={formData.cost_threshold_tokens}
                onChange={(e) => handleChange('cost_threshold_tokens', parseInt(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 1000, min: 0 }}
                helperText="0 = no tiered pricing"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Input Cost above threshold"
                value={formData.input_cost_above_threshold}
                onChange={(e) => handleChange('input_cost_above_threshold', parseFloat(e.target.value) || 0)}
                disabled={isLoading || !formData.cost_threshold_tokens}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="USD per 1M input tokens (tier 2)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Output Cost above threshold"
                value={formData.output_cost_above_threshold}
                onChange={(e) => handleChange('output_cost_above_threshold', parseFloat(e.target.value) || 0)}
                disabled={isLoading || !formData.cost_threshold_tokens}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="USD per 1M output tokens (tier 2)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={14}
                label="Config JSON"
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                disabled={isLoading}
                helperText="Optional JSON config for provider-specific settings. Audio support is controlled by the dedicated switch above."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={14}
                label="Capability Metadata JSON"
                value={capabilityMetadataJson}
                onChange={(e) => setCapabilityMetadataJson(e.target.value)}
                disabled={isLoading}
                helperText='Routing-only JSON, for example {"capabilities":["text_generation","market_reasoning"],"quality_tier":"standard","cost_tier":"economy","context_window_tokens":128000,"max_output_tokens":4096}'
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.audio_input_supported === true}
                      onChange={(e) => handleChange('audio_input_supported', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Direct audio input"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  Enable only after this exact model has been verified to accept audio attachments in chat.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving...' : model ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LlmModelForm;
